import { h, Component } from 'preact';
import Store from 'store/store';
import { loadMovieLocales } from 'utils/baseMovies';
import { BASE_REGION_CODE, FOCUS_STUDIO_SLUG, UNIVERSAL_STUDIO_SLUG } from 'utils/constants';
import { getFlag, getStudioLogo } from 'utils/icons';
import { changeRegion } from 'utils/login';
import { alert } from 'utils/modal';
import { hasGlobalAdminAccess } from 'utils/roles';
import { joinClasses } from 'utils/utils';

import Button from 'components/button/button';
import Dropdown from 'components/dropdown/dropdown';
import EditIcon from 'components/icons/edit';
import { showManageLocaleModal } from 'components/movies/manageLocaleModal';

import s from 'components/regionSelector/regionSelector.sss';

export default class RegionSelector extends Component {
	constructor(props) {
		super(props);

		const { regions, selectedRegion } = Store.get().domains || {};
		const selectedStudio = regions.find(r => r.region_id === selectedRegion).studio_id;
		this.state = { filteredRegions: regions, regions, selectedRegion, selectedStudio };

		this.updateFilteredRegions();

		this.onStudioChange = this.onStudioChange.bind(this);
		this.onRegionChange = this.onRegionChange.bind(this);
		this.onManageLocalesClick = this.onManageLocalesClick.bind(this);
		this.onMovieLocalesUpdate = this.onMovieLocalesUpdate.bind(this);
	}

	componentDidMount() {
		Store.on('UPDATE_MOVIE_LOCALES', this.onMovieLocalesUpdate);
	}

	componentWillUnmount() {
		Store.off('UPDATE_MOVIE_LOCALES', this.onMovieLocalesUpdate);
	}

	onMovieLocalesUpdate(baseMovieId) {
		// Refresh if this is the movie we're showing
		if (this.props.movie === baseMovieId) {
			this.updateFilteredRegions();
		}
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.movie !== this.props.movie) {
			this.updateFilteredRegions(nextProps);
		}
	}

	async updateFilteredRegions(props = this.props) {
		const { regions, selectedStudio } = this.state;
		if (!regions || !selectedStudio) return;

		let filteredRegions = regions.filter(r => r.studio_id === selectedStudio);
		let movieLocaleData = null;

		const { movie } = props;
		if (movie) {
			movieLocaleData = await loadMovieLocales(movie);
			
			// Only show regions that have the movie available, or the base region
			const availableRegionIds = movieLocaleData.locales.filter(r => !!r.localised_movie).map(r => r.id);
			filteredRegions = filteredRegions.filter(r =>
				r.region_code === BASE_REGION_CODE // Always show base region
				|| availableRegionIds.includes(r.region_id) // Show regions that have the movie available
			);
		}

		this.setState({ filteredRegions, movieLocaleData });
	}

	async updateSelectedStudio(selectedStudio) {
		this.setState({ selectedStudio });

		// Automatically select first region for new studio
		const selectedRegion = this.state.regions.find(r => r.studio_id === selectedStudio).region_id;
		await this.updateSelectedRegion(selectedRegion);
	}

	async updateSelectedRegion(regionId) {
		const previousRegion = this.state.selectedRegion;
		this.setState({ selectedRegion: regionId });

		try {
			const selectedRegion = await changeRegion(regionId);
			this.updateFilteredRegions();

			const selectedRegionObj = this.state.regions.find(r => r.region_id === selectedRegion);
			const studioSlug = selectedRegionObj.studio_slug;
			const regionCode = selectedRegionObj.region_code;
			const isUniversal = studioSlug === UNIVERSAL_STUDIO_SLUG;
			const isFocus = studioSlug === FOCUS_STUDIO_SLUG;

			Store.get().domains.set({ selectedRegion, studioSlug, regionCode, isUniversal, isFocus }).now();
			Store.emit('RELOAD');
			Store.emit('UPDATE_MEDIA', true /* refresh */);
			this.props.select?.();
		} catch (e) {
			// Revert to previous region on error
			const previousStudio = this.state.regions.find(r => r.region_id === previousRegion).studio_id;
			this.setState({ selectedRegion: previousRegion, selectedStudio: previousStudio });

			alert('Sorry, an error occured while changing region. Please try again.');
		}
	}

	onStudioChange(e) {
		this.updateSelectedStudio(e.target.value);
	}

	onRegionChange(e) {
		this.updateSelectedRegion(e.target.value);
	}

	onManageLocalesClick() {
		showManageLocaleModal(this.props.movie);
	}

	renderStudioSelector() {
		const studiosDict = this.state.regions.reduce((acc, region) => {
			if (acc[region.studio_id]) return acc;
			acc[region.studio_id] = {
				value: region.studio_id,
				label: region.studio_name,
				img: getStudioLogo(region.studio_slug),
			};
			return acc;
		}, {});
		const studios = Object.values(studiosDict);

		if (studios.length === 1) {
			const studio = studios[0];
			return (
				<div class={joinClasses(s.option, s.studioSelector)}>
					<img class={s.studioLogo} src={studio.img} />
					<p>{studio.label}</p>
				</div>
			);
		}

		return (
			<Dropdown
				name="studio-select"
				class={joinClasses(s.dropdown, s.studioSelector)}
				value={this.state.selectedStudio}
				onChange={this.onStudioChange}
				align="full"
			>
				{studios.map(opt => (
					<option value={opt.value} class={s.option}>
						<img class={s.studioLogo} src={opt.img} />
						<p>{opt.label}</p>
					</option>
				))}
			</Dropdown>
		);
	}

	renderRegionSelector() {
		const showManageLocales = this.props.movie && hasGlobalAdminAccess();
		const regions = this.state.filteredRegions
			.sort((a, b) => a.region_code.localeCompare(b.region_code))
			.map(region => ({
				value: region.region_id,
				label: region.region_name,
				code: region.region_code,
			}));

		if (!showManageLocales && regions.length === 1) {
			const region = regions[0];
			return (
				<div class={s.option}>
					{getFlag(region.code, { class: s.flag })}
					<p>{region.label}</p>
				</div>
			);
		}

		return (
			<Dropdown
				name="region-select"
				class={joinClasses(s.dropdown, s.regionSelector)}
				value={this.state.selectedRegion}
				onChange={this.onRegionChange}
				align="full"
				searchPlaceholder="Search locales..."
				hideSearchDivider={showManageLocales}
				searchable
			>
				{showManageLocales && (
					<Button class={joinClasses(s.option, s.manageLocalesButton)} onClick={this.onManageLocalesClick} text>
						<EditIcon class={s.flag} />
						<p>Manage locales</p>
					</Button>
				)}
				<hr />
				{regions.map(opt => (
					<option value={opt.value} class={s.option}>
						{getFlag(opt.code, { class: s.flag })}
						<p>{opt.label}</p>
					</option>
				))}
			</Dropdown>
		);
	}

	render() {
		const { dark, movie } = this.props;
		return (
			<div class={joinClasses(s.wrapper, dark && s.dark, this.props.class)}>
				{!movie && this.renderStudioSelector()}
				{this.renderRegionSelector()}
			</div>
		);
	}
}
